import type { Editor, Operation as SlateOperation } from 'slate';
import UndoEntry from './UndoEntry';
import type CollaborativeDocument from './CollaborativeDocument';
import EditorState, { Namespace, HistoryKey } from '../util/EditorState';

import Debug from 'debug';
import { Operation } from '../ot-slate-v2';

const debug = Debug('slate-collaboration:history');

const MAXIMUM_UNDO = 100;

class History {
  // The top of the zero is the first entry in each array, so use shift/unshift
  // rather than push/pop.
  undoEntries: UndoEntry[] = [];
  redoEntries: UndoEntry[] = [];

  // Collaborative document we will receive remote operations from.
  collaborativeDocument: CollaborativeDocument;

  // Keep track if we are adding operations to a previous undo entry.
  addingUndo: boolean = false;

  /**
   * Set the history instance for an editor.
   *
   * @param {Editor} editor
   * @param {History} history
   */
  static setForEditor(editor: Editor, history: History) {
    EditorState.set(editor, Namespace.History, HistoryKey.Instance, history);
  }

  /**
   * Retrieve the history instance for an editor.
   *
   * @param {Editor} editor
   * @returns {History}
   */
  static getForEditor(editor: Editor) {
    return EditorState.get(editor, Namespace.History, HistoryKey.Instance);
  }

  /**
   * Undo the last group of operations for an editor.
   *
   * @param {Editor} editor
   */
  static undo(editor: Editor) {
    const history = History.getForEditor(editor);
    if (history) {
      history.undo(editor);
    }
  }

  /**
   * Redo the last group of operations for an editor.
   *
   * @param {Editor} editor
   */
  static redo(editor: Editor) {
    const history = History.getForEditor(editor);
    if (history) {
      history.redo(editor);
    }
  }

  /**
   * When changes are prevented from reaching the undo stack, the undo
   * stack needs to act as if those changes have happened in order to
   * undo themselves correcly. Transforming the undo stack to take those
   * changes into account is the correct behavior most of the time.
   *
   * Sometimes we just want to apply operations directly to the value,
   * as if the undo stack doesn't exist. For example, collaborative
   * editing applying operations to bring its version of the value up to
   * date, or during OT to prevent recursive transformation.
   *
   * This prevents transforming the undo stack when running a change
   * that's prevented from reaching the stack.
   *
   * @param {Editor} editor
   * @param {Block} [fn] A function to run. If inside this function an operation skips the undo stack, the undo stack will not transform based on it.
   */
  static withoutUndoTransforming(editor: Editor, fn: (editor: Editor) => void) {
    const oldValue = EditorState.get(
      editor,
      Namespace.History,
      HistoryKey.Transforming
    );
    EditorState.set(editor, Namespace.History, HistoryKey.Transforming, false);
    if (fn) {
      fn(editor);
      EditorState.set(
        editor,
        Namespace.History,
        HistoryKey.Transforming,
        oldValue
      );
    }
  }

  /**
   * Perform the options in `fn` without saving anything to the history stack.
   *
   * @param {Editor} editor
   * @param {Block} [fn] A function to run without saving undo.
   */
  static withoutUndoSaving(editor: Editor, fn: (editor: Editor) => void) {
    const oldValue = EditorState.get(
      editor,
      Namespace.History,
      HistoryKey.Saving
    );
    EditorState.set(editor, Namespace.History, HistoryKey.Saving, false);
    if (fn) {
      fn(editor);
      EditorState.set(editor, Namespace.History, HistoryKey.Saving, oldValue);
    }
  }

  /**
   * Perform the options in `fn` without merging with previous undos.
   *
   * @param {Editor} editor
   * @param {Block} [fn] A function to run without saving undo.
   */
  static withoutUndoMerging(editor: Editor, fn: (editor: Editor) => void) {
    const oldValue = EditorState.get(
      editor,
      Namespace.History,
      HistoryKey.Merging
    );
    EditorState.set(editor, Namespace.History, HistoryKey.Merging, false);
    if (fn) {
      fn(editor);
      EditorState.set(editor, Namespace.History, HistoryKey.Merging, oldValue);
    }
  }

  /**
   * Perform the options in `fn` forcing merging with previous undos.
   *
   * @param {Editor} editor
   * @param {Block} [fn] A function to run without saving undo.
   */
  static withUndoMerging(editor: Editor, fn: (editor: Editor) => void) {
    const oldValue = EditorState.get(
      editor,
      Namespace.History,
      HistoryKey.Merging
    );
    EditorState.set(editor, Namespace.History, HistoryKey.Merging, true);
    if (fn) {
      fn(editor);
      EditorState.set(editor, Namespace.History, HistoryKey.Merging, oldValue);
    }
  }

  constructor(collaborativeDocument) {
    if (collaborativeDocument) {
      this.collaborativeDocument = collaborativeDocument;
      this.collaborativeDocument.onRemoteOperationsReceived((operations) => {
        this._handleRemoteOperations(operations);
      });
    }
  }

  reset() {
    this.undoEntries = [];
    this.redoEntries = [];
  }

  // Save a single operation to the history stack.
  save(editor: Editor, operation: SlateOperation) {
    const saveUndo = EditorState.get(
      editor,
      Namespace.History,
      HistoryKey.Saving
    );
    const transformUndo = EditorState.get(
      editor,
      Namespace.History,
      HistoryKey.Transforming
    );
    const merge = EditorState.get(
      editor,
      Namespace.History,
      HistoryKey.Merging
    );

    if (saveUndo === false) {
      // If we don't save to the undo stack, the undo stack still
      // needs to take into account the effect of our operations.
      // This works exactly the same way as if the ignored operations
      // were remote, so they can go through that same path.
      if (transformUndo !== false) {
        History.withoutUndoTransforming(editor, () => {
          this._handleRemoteOperations(operation);
        });
      }

      return this;
    }

    this._pushOperation(
      editor,
      // TODO: Operation.inverse will eventually always return an operation, this
      // explicit cast can be removed then
      Operation.inverse(operation) as SlateOperation,
      {
        saveUndo,
        merge,
      }
    );

    if (debug.enabled) {
      debug(`undo stack: ${JSON.stringify(this.undoEntries, null, 2)}`);
    }

    return this;
  }

  get canUndo() {
    return this.undoEntries.length > 0;
  }

  get canRedo() {
    return this.redoEntries.length > 0;
  }

  undo(editor: Editor) {
    const lastEntry = this.undoEntries.shift();
    if (!lastEntry) return;

    const remoteOperations = lastEntry.transform();

    if (this.undoEntries[0]) {
      this.undoEntries[0].appendRemoteOperations(remoteOperations);
    }

    const inverseOperation = lastEntry.apply(editor);

    this.redoEntries.unshift(inverseOperation);

    return editor;
  }

  redo(editor: Editor) {
    const lastEntry = this.redoEntries.shift();
    if (!lastEntry) return;

    const remoteOperations = lastEntry.transform();

    if (this.redoEntries[0]) {
      this.redoEntries[0].appendRemoteOperations(remoteOperations);
    }

    const inverseOperation = lastEntry.apply(editor);

    this.undoEntries.unshift(inverseOperation);

    return editor;
  }

  // Return the last undo entry that was created. Useful to augment the entry
  // with operations that happen asynchronously.
  lastUndoEntry(): UndoEntry {
    return this.undoEntries[0];
  }

  // Operations performed within func will be added to an existing undoEntry.
  // This is necessary when operations may be separated in time by other
  // unrelated operations. `func` must not be async. `addUndo` must not be
  // re-entered.
  //
  // Added operations can only update properties - they must not move nodes
  // around since that would interfere with transforming the undo stack from
  // remove operations.
  addUndo(editor: Editor, undoEntry: UndoEntry, func: () => void) {
    if (!undoEntry) {
      console.error('No undo entry provided to addUndo');
    }
    if (this.addingUndo) {
      console.error('Must not re-enter addUndo');
    }

    this.addingUndo = true;
    this.undoEntries.unshift(undoEntry);
    try {
      History.withUndoMerging(editor, () => func());
    } finally {
      this.undoEntries.shift();
      this.addingUndo = false;
    }
  }

  // Operations performed within func will be added to a new undoEntry.
  // This is necessary when operations may be separated in time by other
  // unrelated operations. `func` must not be async. `addNewUndo` must not be
  // re-entered.
  //
  // Added operations can only update properties - they must not move nodes
  // around since that would interfere with transforming the undo stack from
  // remove operations.
  addNewUndo(editor: Editor, func: () => void) {
    if (this.addingUndo) {
      console.error('Must not re-enter addUndo');
    }

    const undoEntry = new UndoEntry();

    this.addingUndo = true;
    this.undoEntries.unshift(undoEntry);
    try {
      History.withUndoMerging(editor, () => func());
    } finally {
      this.addingUndo = false;
    }
  }

  _pushOperation(editor: Editor, operation: SlateOperation, options) {
    const { merge } = options;

    // We'll always try to compose, unless we've specifically been told
    // not to.
    const shouldCompose = merge !== false;

    // If `merge` is non-commital, and this is not the first
    // operation in a new editor, then append. This will create a
    // single undo step for a group of operations in a single change.
    let shouldAppend = merge !== false;
    if (merge === null || merge === undefined) {
      shouldAppend = editor.operations.length !== 0;
    }

    const allSetSelection = operation.type === 'set_selection';

    const prevUndoEntry: UndoEntry = this.undoEntries[0];
    let handled = false;

    if (prevUndoEntry && shouldCompose) {
      // Try composing with the previous entry to keep the undo stack as
      // small as possible.
      handled = prevUndoEntry.tryCompose(operation);
    }

    if (!handled && prevUndoEntry && shouldAppend) {
      // We couldn't compose, so try appending instead.
      handled = prevUndoEntry.append(operation);
    }

    if (!handled && !allSetSelection) {
      // Create a new undo entry, as long as the operation is something other
      // than just moving the selection.
      const newUndoEntry = new UndoEntry([operation]);
      this.undoEntries.unshift(newUndoEntry);
    }

    // Limit the undo stack size.
    if (this.undoEntries.length > MAXIMUM_UNDO) {
      this.undoEntries.splice(0, MAXIMUM_UNDO);
    }

    // Don't clear the redo stack if we've only moved the selection
    if (!allSetSelection) {
      this.redoEntries = [];
    }
  }

  _handleRemoteOperations(operations: SlateOperation | SlateOperation[]) {
    if (!Array.isArray(operations)) {
      operations = [operations];
    }

    if (operations.length === 0) {
      return;
    }

    const firstUndo = this.undoEntries[0];
    if (firstUndo) {
      firstUndo.appendRemoteOperations(operations);
      // in order to allow composing onto the undo stack, we'll
      // pre-transform the head of the stack. If we don't do this, we
      // won't be able to make changes, get a remote operation, and
      // compose new changes onto those original changes, because
      // they'll have been sourced from different document states. One
      // before the remote operations, and one afterward.
      const remoteOperations = firstUndo.transform();
      if (this.undoEntries.length > 1) {
        this.undoEntries[1].appendRemoteOperations(remoteOperations);
      }
    }

    const firstRedo = this.redoEntries[0];
    if (firstRedo) {
      firstRedo.appendRemoteOperations(operations);
    }
  }
}

export default History;
